import React, { Component } from "react";
import {
  Container,
  Box,
  Grid,
  Typography,
  Card,
  CardContent,
  Button,
  Stack,
  Chip
} from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import GroupsIcon from '@mui/icons-material/Groups';
import HandshakeIcon from '@mui/icons-material/Handshake';
import EventIcon from '@mui/icons-material/Event';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import Footer from '../core/Footer';

// Infinite scroll animation
const scroll = keyframes`
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(-50%);
  }
`;

const LogoScrollContainer = styled(Box)(({ theme }) => ({
  overflow: 'hidden',
  width: '100%',
  backgroundColor: '#f8f9fa',
  padding: '40px 0',
  position: 'relative',
  '&::before, &::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    width: '100px',
    height: '100%',
    zIndex: 2,
  },
  '&::before': {
    left: 0,
    background: 'linear-gradient(to right, #f8f9fa, transparent)',
  },
  '&::after': {
    right: 0,
    background: 'linear-gradient(to left, #f8f9fa, transparent)',
  }
}));

const LogoTrack = styled(Box)({
  display: 'flex',
  animation: `${scroll} 10s linear infinite`,
  '&:hover': {
    animationPlayState: 'paused'
  }
});

const LogoItem = styled(Box)(({ theme }) => ({
  minWidth: '220px',
  height: '120px',
  margin: '0 30px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'white',
  borderRadius: '12px',
  padding: '20px 15px',
  boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
  },
  '& img': {
    maxWidth: '100%',
    maxHeight: '60px',
    objectFit: 'contain',
    filter: 'grayscale(100%)',
    transition: 'filter 0.3s ease',
    marginBottom: '8px',
  },
  '&:hover img': {
    filter: 'grayscale(0%)',
  },
  '& a': {
    textDecoration: 'none',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: '100%',
  },
  '& .member-name': {
    fontSize: '0.875rem',
    fontWeight: 600,
    color: '#666',
    textAlign: 'center',
    marginTop: '8px',
    transition: 'color 0.3s ease',
  },
  '&:hover .member-name': {
    color: '#003E7E',
  }
}));

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  borderRadius: '16px',
  overflow: 'hidden',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: '0 12px 24px rgba(0,0,0,0.15)',
  }
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  width: '64px',
  height: '64px',
  borderRadius: '16px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: '16px',
  background: 'linear-gradient(135deg, #FF6600 0%, #FF8C42 100%)',
  boxShadow: '0 4px 12px rgba(255, 102, 0, 0.3)',
}));

// Sample member logos - shortened list for demonstration
const memberLogos = [
  { companyLogo: 'https://t2.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=http://autodesk.com&size=128', link: 'https://autodesk.com', name: 'Autodesk' },
  { companyLogo: 'https://t2.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=http://bentley.com&size=128', link: 'https://bentley.com', name: 'Bentley' },
  { companyLogo: 'https://t2.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=http://aep.com&size=128', link: 'https://aep.com', name: 'AEP' },
  { companyLogo: 'https://t2.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=http://aecom.com&size=128', link: 'https://aecom.com', name: 'AECOM' },
  { companyLogo: 'https://t2.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=http://ameren.com&size=128', link: 'https://ameren.com', name: 'Ameren' },
  { companyLogo: 'https://t2.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=http://duke-energy.com&size=128', link: 'https://duke-energy.com', name: 'Duke Energy' },
  { companyLogo: 'https://t2.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=http://dominion.com&size=128', link: 'https://dominion.com', name: 'Dominion' },
  { companyLogo: 'https://t2.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=http://exelon.com&size=128', link: 'https://exelon.com', name: 'Exelon' },
  { companyLogo: 'https://t2.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=http://firstenergycorp.com&size=128', link: 'https://firstenergycorp.com', name: 'FirstEnergy' },
  { companyLogo: 'https://t2.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=http://pge.com&size=128', link: 'https://pge.com', name: 'PG&E' },
  { companyLogo: 'https://t2.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=http://sce.com&size=128', link: 'https://sce.com', name: 'SCE' },
  { companyLogo: 'https://t2.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=http://xcelenergy.com&size=128', link: 'https://xcelenergy.com', name: 'Xcel Energy' },
  { companyLogo: 'https://t2.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=http://nationalgrid.com&size=128', link: 'https://nationalgrid.com', name: 'National Grid' },
  { companyLogo: 'https://t2.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=http://aps.com&size=128', link: 'https://aps.com', name: 'APS' },
  { companyLogo: 'https://t2.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=http://burnsmcd.com&size=128', link: 'https://burnsmcd.com', name: 'Burns & McDonnell' },
];

class PublicHome extends Component {

    componentDidMount = () => {
        localStorage.removeItem('jwt');
        localStorage.removeItem('rememberMe');
        localStorage.removeItem('email');
    }

    render() {
        // Duplicate logos for infinite scroll effect
        const duplicatedLogos = [...memberLogos, ...memberLogos];

        return (
            <>
                {/* Hero Section with Gradient */}
                <Box
                    sx={{
                        background: 'linear-gradient(135deg, #003E7E 0%, #0056A8 50%, #FF6600 100%)',
                        color: 'white',
                        py: { xs: 8, md: 12 },
                        position: 'relative',
                        overflow: 'hidden',
                    }}
                >
                    {/* Decorative background circles */}
                    <Box
                        sx={{
                            position: 'absolute',
                            width: { xs: '200px', md: '400px' },
                            height: { xs: '200px', md: '400px' },
                            borderRadius: '50%',
                            background: 'rgba(255, 255, 255, 0.05)',
                            top: '-100px',
                            right: '-100px',
                        }}
                    />
                    <Box
                        sx={{
                            position: 'absolute',
                            width: { xs: '150px', md: '300px' },
                            height: { xs: '150px', md: '300px' },
                            borderRadius: '50%',
                            background: 'rgba(255, 255, 255, 0.05)',
                            bottom: '-50px',
                            left: '-50px',
                        }}
                    />

                    <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
                        <Grid container spacing={4} alignItems="center">
                            <Grid item xs={12} md={8}>
                                <Chip
                                    label="Industry Consortium"
                                    sx={{
                                        mb: 3,
                                        backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                        color: 'white',
                                        fontWeight: 600,
                                        backdropFilter: 'blur(10px)',
                                    }}
                                />
                                <Typography
                                    variant="h2"
                                    component="h1"
                                    sx={{
                                        fontWeight: 700,
                                        mb: 3,
                                        fontSize: { xs: '2.5rem', md: '3.5rem' },
                                        lineHeight: 1.2,
                                    }}
                                >
                                    Substation Design Solution Industry Consortium
                                </Typography>
                                <Typography
                                    variant="h5"
                                    sx={{
                                        mb: 4,
                                        opacity: 0.95,
                                        fontWeight: 400,
                                        lineHeight: 1.6,
                                    }}
                                >
                                    Driving innovation and standardization in utility substation design through collaborative member engagement and shared expertise.
                                </Typography>
                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                    <Button
                                        variant="contained"
                                        size="large"
                                        href="/signup"
                                        endIcon={<ArrowForwardIcon />}
                                        sx={{
                                            backgroundColor: '#FF6600',
                                            color: 'white',
                                            px: 4,
                                            py: 1.5,
                                            fontSize: '1.1rem',
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            borderRadius: '50px',
                                            boxShadow: '0 4px 14px rgba(255, 102, 0, 0.4)',
                                            '&:hover': {
                                                backgroundColor: '#e55a00',
                                                transform: 'translateY(-2px)',
                                                boxShadow: '0 6px 20px rgba(255, 102, 0, 0.5)',
                                            }
                                        }}
                                    >
                                        Join the Consortium
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        size="large"
                                        href="/about"
                                        sx={{
                                            color: 'white',
                                            borderColor: 'white',
                                            px: 4,
                                            py: 1.5,
                                            fontSize: '1.1rem',
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            borderRadius: '50px',
                                            borderWidth: '2px',
                                            '&:hover': {
                                                borderColor: 'white',
                                                borderWidth: '2px',
                                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                                transform: 'translateY(-2px)',
                                            }
                                        }}
                                    >
                                        Learn More
                                    </Button>
                                </Stack>
                            </Grid>
                        </Grid>
                    </Container>
                </Box>

                {/* Member Logos Scrolling Section */}
                <LogoScrollContainer>
                    <Container maxWidth={false} sx={{ mb: 2 }}>
                        <Typography
                            variant="h6"
                            align="center"
                            sx={{
                                color: '#666',
                                fontWeight: 600,
                                mb: 3,
                                textTransform: 'uppercase',
                                letterSpacing: '2px',
                            }}
                        >
                            Just a few of our members
                        </Typography>
                    </Container>
                    <LogoTrack>
                        {duplicatedLogos.map((member, index) => (
                            <LogoItem key={index}>
                                <a href={member.link} target="_blank" rel="noopener noreferrer">
                                    <img src={member.companyLogo} alt={member.name} />
                                    <Typography className="member-name">
                                        {member.name}
                                    </Typography>
                                </a>
                            </LogoItem>
                        ))}
                    </LogoTrack>
                </LogoScrollContainer>

                {/* Features Section */}
                <Container maxWidth="lg" sx={{ py: 8 }}>
                    <Box sx={{ textAlign: 'center', mb: 6 }}>
                        <Typography
                            variant="h3"
                            component="h2"
                            sx={{
                                fontWeight: 700,
                                mb: 2,
                                color: '#003E7E',
                            }}
                        >
                            How We Work Together
                        </Typography>
                        <Typography
                            variant="h6"
                            sx={{
                                color: '#666',
                                maxWidth: '800px',
                                mx: 'auto',
                            }}
                        >
                            Collaborative initiatives driving innovation across the industry
                        </Typography>
                    </Box>

                    <Grid container spacing={4}>
                        {/* Committees Card */}
                        <Grid item xs={12} md={4}>
                            <StyledCard elevation={2}>
                                <CardContent sx={{ p: 4, flexGrow: 1 }}>
                                    <IconWrapper>
                                        <GroupsIcon sx={{ fontSize: 36, color: 'white' }} />
                                    </IconWrapper>
                                    <Typography
                                        variant="h5"
                                        component="h3"
                                        sx={{
                                            fontWeight: 700,
                                            mb: 2,
                                            color: '#003E7E',
                                        }}
                                    >
                                        Committees
                                    </Typography>
                                    <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.8 }}>
                                        Multiple committees run by utility member volunteers with participation from engineering firms, utilities, and equipment vendors. Producing best practices, recommendations, and workflow examples to advance design disciplines.
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        href="/signup"
                                        endIcon={<ArrowForwardIcon />}
                                        fullWidth
                                        sx={{
                                            backgroundColor: '#FF6600',
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            py: 1.5,
                                            borderRadius: '12px',
                                            '&:hover': {
                                                backgroundColor: '#e55a00',
                                            }
                                        }}
                                    >
                                        Get Involved
                                    </Button>
                                </CardContent>
                            </StyledCard>
                        </Grid>

                        {/* Collaboration Card */}
                        <Grid item xs={12} md={4}>
                            <StyledCard elevation={2}>
                                <CardContent sx={{ p: 4, flexGrow: 1 }}>
                                    <IconWrapper>
                                        <HandshakeIcon sx={{ fontSize: 36, color: 'white' }} />
                                    </IconWrapper>
                                    <Typography
                                        variant="h5"
                                        component="h3"
                                        sx={{
                                            fontWeight: 700,
                                            mb: 2,
                                            color: '#003E7E',
                                        }}
                                    >
                                        Collaboration
                                    </Typography>
                                    <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.8 }}>
                                        Monthly web meetings enable open participation across committees. This collaborative approach produces documentation and provides valuable recommendations to software vendors developing industry tools.
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        href="/signup"
                                        endIcon={<ArrowForwardIcon />}
                                        fullWidth
                                        sx={{
                                            backgroundColor: '#FF6600',
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            py: 1.5,
                                            borderRadius: '12px',
                                            '&:hover': {
                                                backgroundColor: '#e55a00',
                                            }
                                        }}
                                    >
                                        Get Involved
                                    </Button>
                                </CardContent>
                            </StyledCard>
                        </Grid>

                        {/* Face to Face Card */}
                        <Grid item xs={12} md={4}>
                            <StyledCard elevation={2}>
                                <CardContent sx={{ p: 4, flexGrow: 1 }}>
                                    <IconWrapper>
                                        <EventIcon sx={{ fontSize: 36, color: 'white' }} />
                                    </IconWrapper>
                                    <Typography
                                        variant="h5"
                                        component="h3"
                                        sx={{
                                            fontWeight: 700,
                                            mb: 2,
                                            color: '#003E7E',
                                        }}
                                    >
                                        Annual Events
                                    </Typography>
                                    <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.8 }}>
                                        Annual in-person events hosted by member companies provide networking, free training, and valuable industry contacts. TED talk-style presentations attended by utilities, engineering firms, and vendors focused on substation design.
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        href="/signup"
                                        endIcon={<ArrowForwardIcon />}
                                        fullWidth
                                        sx={{
                                            backgroundColor: '#FF6600',
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            py: 1.5,
                                            borderRadius: '12px',
                                            '&:hover': {
                                                backgroundColor: '#e55a00',
                                            }
                                        }}
                                    >
                                        Get Involved
                                    </Button>
                                </CardContent>
                            </StyledCard>
                        </Grid>
                    </Grid>
                </Container>

                {/* Mission Statement Section */}
                <Box sx={{ backgroundColor: '#f8f9fa', py: 8 }}>
                    <Container maxWidth="md">
                        <Typography
                            variant="h4"
                            align="center"
                            sx={{
                                fontWeight: 700,
                                mb: 4,
                                color: '#003E7E',
                            }}
                        >
                            Our Mission
                        </Typography>
                        <Typography
                            variant="body1"
                            align="center"
                            sx={{
                                fontSize: '1.1rem',
                                lineHeight: 1.8,
                                color: '#555',
                                mb: 3,
                            }}
                        >
                            The SDS Industry Consortium brings together member utilities and contract/design firms to establish common workflows, toolkit functionality, shared libraries, and industry standards. We drive technology development according to industry needs and promote common library development from internal resources, external partners, and equipment manufacturers.
                        </Typography>
                        <Box sx={{ textAlign: 'center', mt: 4 }}>
                            <Button
                                variant="contained"
                                size="large"
                                href="/about"
                                sx={{
                                    backgroundColor: '#003E7E',
                                    color: 'white',
                                    px: 4,
                                    py: 1.5,
                                    fontSize: '1.1rem',
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    borderRadius: '50px',
                                    '&:hover': {
                                        backgroundColor: '#00325a',
                                    }
                                }}
                            >
                                Learn More About SDSIC
                            </Button>
                        </Box>
                    </Container>
                </Box>

                <Footer />
            </>
        )
    }
}

export default PublicHome;
